import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config';
import {
  PROFILE_VERTICAL_CONFIGS,
  getRankingSequenceRecommendation,
  getWinnerConfidence,
  getRatingContextForVertical,
} from '@photofeeler/shared';
import { useGameStore } from '../stores/game';
import { WinnerRationale } from '../components/pretext';

interface PhotoResult {
  id: string;
  context: string;
  status: string;
  created_at: string;
  aggregates: { composite_score: number; model_count: number }[];
}

interface RatingDetail {
  model: string;
  score: number;
  traits: string[];
  raw_response?: { attributes?: Record<string, number> };
}

interface PhotoTraits {
  [photoId: string]: Record<string, number>;
}

export function Compare() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { session } = useAuth();
  const { projects, activeProjectId, setActiveProject } = useGameStore();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [photoTraits, setPhotoTraits] = useState<PhotoTraits>({});
  const [loadingTraits, setLoadingTraits] = useState(false);
  const requestedProjectId = searchParams.get('project');
  const activeProject =
    projects.find(project => project.id === requestedProjectId) ||
    projects.find(project => project.id === activeProjectId) ||
    projects.find(project => project.vertical === 'dating') ||
    projects[0];
  const activeConfig = activeProject ? PROFILE_VERTICAL_CONFIGS[activeProject.vertical] : PROFILE_VERTICAL_CONFIGS.dating;
  const activeRatingContext = activeProject ? getRatingContextForVertical(activeProject.vertical) : 'dating';

  const { data, isLoading } = useQuery({
    queryKey: ['photos-compare'],
    queryFn: async () => {
      if (!session?.access_token) return { photos: [] };
      const res = await fetch(`${API_CONFIG.RATE_URL}/photos`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!res.ok) return { photos: [] };
      return res.json() as Promise<{ photos: PhotoResult[] }>;
    },
    enabled: !!session?.access_token,
  });

  const photos = (data?.photos || []).filter(
    p => p.status === 'completed' && p.aggregates?.length > 0
  );
  const visiblePhotos = photos.filter(photo => photo.context === activeRatingContext);
  const selectedPhotos = visiblePhotos.filter(p => selected.has(p.id));
  const bestPhoto =
    selectedPhotos.length > 0
      ? selectedPhotos.reduce((best, p) =>
          p.aggregates[0].composite_score > best.aggregates[0].composite_score ? p : best
        )
      : null;

  useEffect(() => {
    if (activeProject?.id) {
      setActiveProject(activeProject.id);
    }
  }, [activeProject?.id, setActiveProject]);

  useEffect(() => {
    setSelected(new Set());
  }, [activeProject?.id]);

  // Fetch per-trait data for selected photos
  useEffect(() => {
    if (selectedPhotos.length < 2 || !session?.access_token) return;

    const fetchTraits = async () => {
      setLoadingTraits(true);
      const newTraits: PhotoTraits = {};

      await Promise.all(
        selectedPhotos.map(async (p) => {
          if (photoTraits[p.id]) {
            newTraits[p.id] = photoTraits[p.id];
            return;
          }
          try {
            const res = await fetch(`${API_CONFIG.RATE_URL}/ratings/${p.id}`, {
              headers: { Authorization: `Bearer ${session.access_token}` },
            });
            if (!res.ok) return;
            const data = await res.json() as { ratings: RatingDetail[] };
            const attrs: Record<string, number[]> = {};
            for (const r of data.ratings) {
              if (r.raw_response?.attributes) {
                for (const [k, v] of Object.entries(r.raw_response.attributes)) {
                  if (!attrs[k]) attrs[k] = [];
                  attrs[k].push(v);
                }
              }
            }
            newTraits[p.id] = {};
            for (const [k, vals] of Object.entries(attrs)) {
              newTraits[p.id][k] = vals.reduce((a, b) => a + b, 0) / vals.length;
            }
          } catch { /* skip */ }
        })
      );

      setPhotoTraits(prev => ({ ...prev, ...newTraits }));
      setLoadingTraits(false);
    };

    fetchTraits();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Array.from(selected).join(','), session?.access_token]);

  const toggleSelect = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else if (next.size < 5) next.add(id);
    setSelected(next);
  };

  const scoreColor = (s: number) =>
    s >= 80
      ? 'text-emerald-600'
      : s >= 60
        ? 'text-blue-600'
        : s >= 40
          ? 'text-amber-600'
          : 'text-rose';
  const barColor = (s: number) =>
    s >= 7 ? 'bg-emerald-400' : s >= 5 ? 'bg-blue-400' : 'bg-amber-400';
  const contextEmoji: Record<string, string> = { dating: '💕', business: '💼', social: '🎉' };

  // Collect all unique trait names across selected photos
  const allTraitNames = new Set<string>();
  for (const id of selected) {
    if (photoTraits[id]) {
      for (const k of Object.keys(photoTraits[id])) {
        allTraitNames.add(k);
      }
    }
  }
  const traitNames = Array.from(allTraitNames);
  const selectedRankings = selectedPhotos.map(photo => ({
    photoId: photo.id,
    score: photo.aggregates[0].composite_score,
  }));
  const winnerConfidence = selectedPhotos.length >= 2 ? getWinnerConfidence(selectedRankings) : null;
  const sequenceRecommendation =
    selectedPhotos.length >= 2
      ? getRankingSequenceRecommendation(activeProject?.vertical ?? 'dating', selectedRankings)
      : null;
  const rankingTitle =
    activeProject?.vertical === 'linkedin' ? 'Primary headshot recommendation' : 'Lead photo recommendation';
  const rankingPrompt =
    activeProject?.vertical === 'linkedin'
      ? 'Which headshot should lead your LinkedIn presence?'
      : 'Which photo should be your lead image?';

  // Find best trait value per trait across selected photos
  const bestTraitByName: Record<string, string> = {};
  for (const name of traitNames) {
    let bestId = '';
    let bestVal = -1;
    for (const p of selectedPhotos) {
      const val = photoTraits[p.id]?.[name];
      if (val !== undefined && val > bestVal) {
        bestVal = val;
        bestId = p.id;
      }
    }
    if (bestId) bestTraitByName[name] = bestId;
  }

  return (
    <div className="relative overflow-hidden pt-24 pb-16 px-5 sm:px-8 max-w-5xl mx-auto animate-fade-in">
      <div className="page-orb left-[-5rem] top-28 h-72 w-72 bg-rose/15" />
      <div className="page-orb right-[-4rem] top-24 h-80 w-80 bg-info/10" />

      <div className="page-hero-panel mb-10">
        <div className="flex items-start justify-between gap-5">
          <div>
            <div className="premium-kicker mb-3">Side-by-side compare</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink" style={{ fontWeight: 600 }}>
              Compare photos
            </h1>
            <p className="hero-copy mt-3 max-w-2xl">
              {activeProject?.vertical === 'dating'
                ? 'Compare your dating photos side by side, find the strongest lead image, and decide your lineup order.'
                : activeProject?.vertical === 'linkedin'
                  ? 'Compare your headshot options side by side, find the strongest professional impression, and choose your profile photo.'
                  : `Compare completed ${activeConfig.shortLabel.toLowerCase()} analyses side by side, isolate the winner, and keep the workspace moving.`}
            </p>
          </div>
          <button onClick={() => navigate('/app')} className="btn-ghost text-xs shrink-0">
            ← Back
          </button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mt-6">
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{activeConfig.label}</div>
            <div className="text-xs text-ink-faint mt-1">Active workspace</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>{visiblePhotos.length}</div>
            <div className="text-xs text-ink-faint mt-1">Completed photos</div>
          </div>
          <div className="premium-stat">
            <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>Up to 5</div>
            <div className="text-xs text-ink-faint mt-1">Photos per compare</div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="skeleton h-16 rounded-xl" />
          ))}
        </div>
      ) : visiblePhotos.length < 2 ? (
        <div className="card text-center py-14">
          <div className="text-4xl mb-4">📊</div>
          <h3 className="font-sans text-xl text-ink mb-2">Almost there</h3>
          <p className="text-sm text-ink-faint mb-2" style={{ fontWeight: 300 }}>
            You need at least 2 completed {activeConfig.shortLabel.toLowerCase()} analyses to compare side by side.
          </p>
          <p className="text-xs text-ink-faint mb-6" style={{ fontWeight: 300 }}>
            Tip: use compare to rank a full {activeConfig.shortLabel.toLowerCase()} set in one pass.
          </p>
          <div className="flex justify-center gap-3">
            <button onClick={() => navigate('/app')} className="btn-primary text-xs">Upload a photo</button>
            <button onClick={() => navigate(`/compare?vertical=${activeProject?.vertical || 'dating'}`)} className="btn-secondary text-xs">Compare</button>
          </div>
        </div>
      ) : (
        <>
          {activeProject?.compareRuns.length ? (
            <div className="card mb-8 lift-hover">
              <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-3">
                Recent compare runs
              </div>
              <div className="space-y-3">
                {activeProject.compareRuns.slice(-3).reverse().map(run => (
                  <div key={run.id} className="flex items-center justify-between gap-4 rounded-xl border border-surface-border bg-surface p-3">
                    <div>
                      <div className="text-sm font-sans font-medium text-ink">
                        Winner {run.winnerScore != null ? `${run.winnerScore}` : '--'}
                      </div>
                      <div className="text-[11px] text-ink-faint" style={{ fontWeight: 300 }}>
                        {run.rankings.length} ranked photo{run.rankings.length === 1 ? '' : 's'} · {new Date(run.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <button
                      onClick={() => navigate(run.winnerPhotoId ? `/results/${run.winnerPhotoId}` : '/app')}
                      className="btn-ghost text-[11px]"
                    >
                      View winner
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Selection */}
          <div className="space-y-2 mb-10">
            <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-3">
              Select photos to compare ({selected.size}/5)
            </div>
            {visiblePhotos.map(p => {
              const isSelected = selected.has(p.id);
              const score = p.aggregates[0].composite_score;
              return (
                <div
                  key={p.id}
                  onClick={() => toggleSelect(p.id)}
                  className={`card-hover lift-hover flex items-center gap-4 p-4 ${isSelected ? 'ring-1 ring-rose/40 bg-rose-faint' : ''}`}
                >
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'border-rose bg-rose text-white' : 'border-surface-border'}`}
                  >
                    {isSelected && <span className="text-[10px]">✓</span>}
                  </div>
                  <div className="w-10 h-10 bg-cream-warm rounded-lg flex items-center justify-center text-sm">
                    {contextEmoji[p.context] || '📷'}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-sans font-medium text-ink">
                      {p.context.charAt(0).toUpperCase() + p.context.slice(1)}
                    </div>
                    <div className="text-[11px] text-ink-faint">
                      {new Date(p.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div
                    className={`text-lg font-sans ${scoreColor(score)}`}
                    style={{ fontWeight: 600 }}
                  >
                    {score}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Comparison grid */}
          {selectedPhotos.length >= 2 && (
            <div>
              <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-4">
                Comparison
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${Math.min(selectedPhotos.length, 5)}, 1fr)`,
                  gap: '1rem',
                }}
              >
                {selectedPhotos.map(p => {
                  const score = p.aggregates[0].composite_score;
                  const isBest = bestPhoto?.id === p.id;
                  return (
                    <div key={p.id} className={`card text-center ${isBest ? 'card-rose' : ''}`}>
                      {isBest && (
                        <div className="text-[10px] font-sans font-medium text-rose mb-2">
                          ★ Best
                        </div>
                      )}
                      <div className="text-2xl mb-1">{contextEmoji[p.context] || '📷'}</div>
                      <div className="text-xs font-sans text-ink-muted mb-3">
                        {p.context.charAt(0).toUpperCase() + p.context.slice(1)}
                      </div>
                      <div
                        className={`text-4xl font-sans ${scoreColor(score)} mb-1`}
                        style={{ fontWeight: 600 }}
                      >
                        {score}
                      </div>
                      <div className="text-[10px] text-ink-faint">
                        {p.aggregates[0].model_count} models
                      </div>
                      <button
                        onClick={() => navigate(`/results/${p.id}`)}
                        className="btn-ghost text-[10px] mt-3"
                      >
                        View details →
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Head-to-Head Prediction (A/B simulator) */}
              {selectedPhotos.length === 2 && (
                <div className="card p-6 mt-8 text-center">
                  <h3 className="font-sans text-lg text-ink mb-2" style={{ fontWeight: 600 }}>
                    {rankingTitle}
                  </h3>
                  <p className="text-sm text-ink-muted mb-4">{rankingPrompt}</p>
                  {(() => {
                    const s1 = selectedPhotos[0].aggregates[0].composite_score;
                    const s2 = selectedPhotos[1].aggregates[0].composite_score;
                    const winner = s1 >= s2 ? 0 : 1;
                    const diff = Math.abs(s1 - s2);
                    return (
                      <div>
                        <div className="text-2xl mb-2">{winner === 0 ? '\u2B05\uFE0F' : '\u27A1\uFE0F'}</div>
                        <p className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>
                          {activeProject?.vertical === 'linkedin'
                            ? `Photo ${winner === 0 ? 'A' : 'B'} should be your primary headshot`
                            : `Photo ${winner === 0 ? 'A' : 'B'} should be your lead image`}
                        </p>
                        <p className="text-xs text-ink-muted mt-1">
                          {winnerConfidence?.label ?? (diff > 10 ? 'Clear winner' : diff > 5 ? 'Moderate edge' : 'Tight call')} &mdash; {diff} point difference
                        </p>
                        <p className="text-xs text-ink-faint mt-3">
                          {activeProject?.vertical === 'linkedin'
                            ? 'Use your highest-scoring headshot on LinkedIn first, then keep the next best option as backup.'
                            : 'Lead with your highest-scoring photo. On dating apps, your first photo determines most swipe decisions.'}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Winner Rationale — pretext editorial wrap */}
              {selectedPhotos.length >= 2 && bestPhoto && winnerConfidence && (() => {
                const loserPhoto = selectedPhotos.find(p => p.id !== bestPhoto.id);
                if (!loserPhoto) return null;

                const winnerTraits = photoTraits[bestPhoto.id];
                const loserTraits = photoTraits[loserPhoto.id];

                // Build trait comparisons from available data
                const traitComps: { trait: string; winner: number; loser: number }[] = [];
                if (winnerTraits && loserTraits) {
                  for (const name of traitNames) {
                    const wVal = winnerTraits[name];
                    const lVal = loserTraits[name];
                    if (wVal !== undefined && lVal !== undefined) {
                      traitComps.push({ trait: name, winner: wVal, loser: lVal });
                    }
                  }
                  // Sort by largest gap first
                  traitComps.sort((a, b) => (b.winner - b.loser) - (a.winner - a.loser));
                }

                const diff = Math.abs(
                  bestPhoto.aggregates[0].composite_score -
                  loserPhoto.aggregates[0].composite_score,
                );

                const rationaleText = `${winnerConfidence.label} — with a ${diff}-point lead, this photo ${
                  activeProject?.vertical === 'linkedin'
                    ? 'projects the stronger professional presence'
                    : 'creates a better first impression'
                }. ${winnerConfidence.detail}`;

                return (
                  <WinnerRationale
                    winnerScore={bestPhoto.aggregates[0].composite_score}
                    rationaleTitle={rankingTitle}
                    rationaleText={rationaleText}
                    traitComparisons={traitComps}
                    vertical={activeProject?.vertical === 'linkedin' ? 'linkedin' : 'dating'}
                    className="mt-8"
                  />
                );
              })()}

              {winnerConfidence && sequenceRecommendation && (
                <div className="grid lg:grid-cols-[1fr_1.2fr] gap-5 mt-8">
                  <div className="card p-5">
                    <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                      Decision confidence
                    </div>
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <h3 className="font-sans text-lg text-ink" style={{ fontWeight: 600 }}>
                        {winnerConfidence.label}
                      </h3>
                      <div className={`rounded-full px-3 py-1 text-[11px] font-sans ${
                        winnerConfidence.level === 'high'
                          ? 'bg-emerald-50 text-emerald-700'
                          : winnerConfidence.level === 'medium'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-amber-50 text-amber-700'
                      }`}>
                        Gap {winnerConfidence.scoreGap}
                      </div>
                    </div>
                    <p className="text-sm text-ink-muted leading-relaxed" style={{ fontWeight: 300 }}>
                      {winnerConfidence.detail}
                    </p>
                  </div>

                  <div className="card p-5">
                    <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-2">
                      {activeProject?.vertical === 'linkedin' ? 'Headshot shortlist' : 'Lineup plan'}
                    </div>
                    <h3 className="font-sans text-lg text-ink mb-1" style={{ fontWeight: 600 }}>
                      {sequenceRecommendation.title}
                    </h3>
                    <p className="text-sm text-ink-muted mb-4" style={{ fontWeight: 300 }}>
                      {sequenceRecommendation.subtitle}
                    </p>
                    <div className="space-y-3">
                      {sequenceRecommendation.items.map(item => {
                        const photo = selectedPhotos.find(selectedPhoto => selectedPhoto.id === item.photoId);
                        return (
                          <div key={item.photoId} className="flex items-start gap-3 rounded-xl border border-surface-border bg-cream-warm px-3 py-3">
                            <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-white text-xs font-sans text-ink">
                              {item.position}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-3">
                                <div className="text-sm font-sans text-ink" style={{ fontWeight: 600 }}>
                                  {item.title}
                                </div>
                                <div className="text-[11px] font-sans text-ink-faint">
                                  {photo?.aggregates[0].composite_score ?? item.score}
                                </div>
                              </div>
                              <p className="text-xs text-ink-faint mt-1 leading-relaxed" style={{ fontWeight: 300 }}>
                                {item.rationale}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Per-trait comparison (Phase 9) */}
              {traitNames.length > 0 && !loadingTraits && (
                <div className="mt-8">
                  <div className="text-[11px] font-sans text-ink-faint tracking-wider uppercase mb-4">
                    Trait breakdown
                  </div>
                  <div className="card overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-surface-border">
                          <th className="text-left py-2 pr-4 text-[11px] font-sans text-ink-faint tracking-wider uppercase" style={{ fontWeight: 500 }}>Trait</th>
                          {selectedPhotos.map(p => (
                            <th key={p.id} className="text-center py-2 px-2 text-[11px] font-sans text-ink-faint tracking-wider uppercase" style={{ fontWeight: 500 }}>
                              {contextEmoji[p.context]} {p.context.charAt(0).toUpperCase() + p.context.slice(1)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {traitNames.map(name => (
                          <tr key={name} className="border-b border-surface-border last:border-0">
                            <td className="py-3 pr-4 text-xs font-sans text-ink-muted capitalize" style={{ fontWeight: 300 }}>{name}</td>
                            {selectedPhotos.map(p => {
                              const val = photoTraits[p.id]?.[name];
                              const isBestTrait = bestTraitByName[name] === p.id && selectedPhotos.length > 1;
                              return (
                                <td key={p.id} className="py-3 px-2 text-center">
                                  {val !== undefined ? (
                                    <div className="flex items-center justify-center gap-2">
                                      <div className="w-16 h-[3px] bg-cream-dark rounded-full overflow-hidden">
                                        <div className={`h-full rounded-full ${barColor(val)}`} style={{ width: `${(val / 10) * 100}%` }} />
                                      </div>
                                      <span className={`text-xs font-sans ${isBestTrait ? 'font-medium text-emerald-600' : 'text-ink-muted'}`}>
                                        {val.toFixed(1)}
                                        {isBestTrait && ' ★'}
                                      </span>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-ink-faint">—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {loadingTraits && (
                <div className="mt-8 text-center">
                  <div className="w-5 h-5 border border-ink/15 border-t-rose rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-xs text-ink-faint">Loading trait data...</p>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
